/**
 * Fantasy Console 0 - Sound Editor Utilities
 */

import type { SFX, SFXNote } from '@fc0/types'
import { NOTES_PER_SFX, SFX_COUNT } from '@fc0/types'

/**
 * Note names for each pitch value (0-63)
 * Pitch 0 = C-1, 12 = C-2, 24 = C-3, etc.
 */
const NOTE_NAMES = [
  'C',
  'C#',
  'D',
  'D#',
  'E',
  'F',
  'F#',
  'G',
  'G#',
  'A',
  'A#',
  'B',
]

export function pitchToNoteName(pitch: number): string {
  const octave = Math.floor(pitch / 12) + 1
  const note = NOTE_NAMES[pitch % 12]
  return `${note}-${octave}`
}

export const WAVEFORM_NAMES = [
  'Sine',
  'Triangle',
  'Sawtooth',
  'Square',
  'Pulse 25%',
  'Pulse 12%',
  'Noise',
  'Rounded Saw',
]

export const EFFECT_NAMES = [
  'None',
  'Slide',
  'Vibrato',
  'Drop',
  'Fade In',
  'Fade Out',
  'Arp Fast',
  'Arp Slow',
]

/**
 * SVG path data for waveform shapes (24x12 viewbox)
 */
export const WAVEFORM_PATHS = [
  'M1 11 Q6 11 6 6 Q6 1 12 1 Q18 1 18 6 Q18 11 23 11', // 0 Sine
  'M1 11 L7 1 L13 11 L19 1 L23 11', // 1 Triangle
  'M1 11 L1 1 L12 11 L12 1 L23 11', // 2 Sawtooth
  'M1 11 L1 1 L12 1 L12 11 L23 11 L23 1', // 3 Square
  'M1 11 L1 1 L7 1 L7 11 L13 11 L13 1 L19 1 L19 11 L23 11', // 4 Pulse 25%
  'M1 11 L1 1 L4 1 L4 11 L13 11 L13 1 L16 1 L16 11 L23 11', // 5 Pulse 12%
  'M1 8 L3 3 L5 9 L7 2 L9 10 L11 4 L13 9 L15 1 L17 11 L19 5 L21 7 L23 3', // 6 Noise
  'M 1 11 L 1 6 Q 1 -3 6.5 6 Q 12 15 12 6 Q 12 -3 18.5 6 L 23 11', // 7 Rounded Saw
]

export const WAVEFORM_COLORS = [
  '#29adff', // 0 Sine: blue
  '#00e436', // 1 Triangle: green
  '#ff004d', // 2 Sawtooth: red
  '#ffa300', // 3 Square: orange
  '#ffec27', // 4 Pulse 25%: yellow
  '#ab5236', // 5 Pulse 12%: brown
  '#83769c', // 6 Noise: lavender
  '#ff77a8', // 7 Rounded Saw: pink
]

const WAVEFORM_COLORS_LIGHT = [
  '#1a7fd4', // 0 Sine: deeper blue
  '#009930', // 1 Triangle: deeper green
  '#cc0033', // 2 Sawtooth: deeper red
  '#cc8200', // 3 Square: deeper orange
  '#b38600', // 4 Pulse 25%: deeper yellow
  '#8a3f26', // 5 Pulse 12%: deeper brown
  '#6b5f82', // 6 Noise: deeper lavender
  '#d4547a', // 7 Rounded Saw: deeper pink
]

export function getWaveformColors(isDark: boolean): string[] {
  return isDark ? WAVEFORM_COLORS : WAVEFORM_COLORS_LIGHT
}

/**
 * Check if an SFX slot has any audible content
 */
export function sfxHasContent(sfx: SFX): boolean {
  return sfx.notes.some((note) => note.volume > 0)
}

/**
 * Deep clone a single SFX
 */
export function cloneSFX(sfx: SFX): SFX {
  return {
    speed: sfx.speed,
    loop_start: sfx.loop_start,
    loop_end: sfx.loop_end,
    notes: sfx.notes.map((note) => ({ ...note })),
  }
}

/**
 * Deep clone an entire SFX bank
 */
export function cloneSFXBank(bank: SFX[]): SFX[] {
  return bank.map(cloneSFX)
}

/**
 * Create an empty SFX note
 */
export function createEmptyNote(): SFXNote {
  return { pitch: 0, waveform: 0, volume: 0, effect: 0 }
}

/**
 * Create an empty SFX
 */
export function createEmptySFX(): SFX {
  const notes: SFXNote[] = []
  for (let i = 0; i < NOTES_PER_SFX; i++) {
    notes.push(createEmptyNote())
  }
  return { speed: 1, loop_start: 0, loop_end: 0, notes }
}

/**
 * Create a bank of 64 empty SFX
 */
export function createEmptySFXBank(): SFX[] {
  const bank: SFX[] = []
  for (let i = 0; i < SFX_COUNT; i++) {
    bank.push(createEmptySFX())
  }
  return bank
}
